import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createRefreshCoordinator, readEfficiencyEnabled, refreshReason, refreshScopeKey } from "./refresh-coordinator.mjs";
import { createClient } from "@supabase/supabase-js";
import { RETAIL_QUOTE_CREDIT_CARD_CHARGE_NOTE } from "./lib/retail-quote-copy";
import { supportTicketEntityScope } from "./support-ticket-scope";
import { explainHttpFailure, explainNetworkFailure, isExpectedHttpFailure } from "./error-guidance.mjs";

const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "::1"]);
const DEVELOPER_DATA_PREF_KEY = "rp:developer-data";
const OWNER_ADMIN_EMAIL = "darren@ranchpower.com";
const API_REQUEST_TIMEOUT_MS = 45000;
const BOOTSTRAP_REQUEST_TIMEOUT_MS = 60000;
const INVENTORY_PIPELINE_BOOTSTRAP_TIMEOUT_MS = 12000;
const AUTH_REQUEST_TIMEOUT_MS = 30000;
const SESSION_ENDED_MESSAGE = "Your secure session ended. Sign in again to continue.";
const OTP_EXPIRED_MESSAGE = "This email sign-in link expired or was already used. Sign in with your password or request a new magic link.";
const SESSION_REFRESH_GRACE_SECONDS = 180;
const REALTIME_REFRESH_DEBOUNCE_MS = 1200;
function isActiveTextEntry() {
  const active = document.activeElement;
  if (!active) return false;
  if (active.isContentEditable || active.tagName === "TEXTAREA" || active.tagName === "SELECT") return true;
  return active.tagName === "INPUT" && !["button", "checkbox", "hidden", "radio", "reset", "submit"].includes((active.getAttribute("type") || "text").toLowerCase());
}
const REALTIME_TABLES = [
  "rp_user_profiles",
  "rp_skus",
  "rp_price_lists",
  "rp_territories",
  "rp_sales_goals",
  "rp_tax_rules",
  "rp_approval_rules",
  "rp_customers",
  "rp_customer_locations",
  "rp_locations",
  "rp_inventory_ledger",
  "rp_stock_requests",
  "rp_stock_request_lines",
  "rp_sales_orders",
  "rp_sales_order_lines",
  "rp_sales_order_adjustments",
  "rp_events",
  "rp_event_team",
  "rp_event_inventory_plan",
  "rp_event_inventory_allocations",
  "rp_event_sales_links",
  "rp_event_closeouts",
  "rp_event_inventory_closeout_receipts",
  "rp_event_inventory_open_case_receipts",
  "rp_shopify_inventory_reconciliations",
  "rp_open_case_batches",
  "rp_open_case_movements",
  "rp_dispatch_routes",
  "rp_dispatch_stops",
  "rp_dispatch_load_checks",
  "rp_expenses",
  "rp_trade_spend_deductions",
  "rp_visit_logs",
  "rp_tasks",
  "rp_email_events",
  "rp_leads",
  "rp_suppliers",
  "rp_materials",
  "rp_material_inventory_ledger",
  "rp_procurement_documents",
  "rp_purchase_orders",
  "rp_purchase_order_lines",
  "rp_production_runs",
  "rp_production_inputs",
  "rp_production_documents",
  "rp_can_production_runs",
  "rp_packaging_runs",
  "rp_packaging_can_allocations",
  "rp_inventory_transfer_logistics",
  "rp_chep_pallet_movements"
];

const normalizeRole = role => {
  const normalized = String(role || "").toLowerCase().replace(/[\s-]+/g, "_");
  const aliases = {
    administrator: "admin",
    admins: "admin",
    superadmin: "admin",
    super_admin: "admin",
    system_admin: "admin",
    operations: "ops",
    operations_manager: "ops",
    founder: "leadership",
    owner: "leadership",
    sales_rep: "rep",
    sales: "rep",
    dispatcher: "dispatch",
    accounting: "accountant",
    finance: "accountant",
    client_account: "customer",
    client: "customer"
  };
  return aliases[normalized] || normalized;
};

const normalizeProfile = profile => profile
  ? { ...profile, role: normalizeRole(profile.role) || normalizeRole(profile.user_metadata?.role) || "rep" }
  : profile;

function isSessionNearExpiry(session) {
  const expiresAt = Number(session?.expires_at || 0);
  if (!expiresAt) return false;
  return (expiresAt * 1000) - Date.now() < SESSION_REFRESH_GRACE_SECONDS * 1000;
}

function publishSession(session) {
  if (typeof window === "undefined" || !session) return session;
  if (window.RP_AUTH) window.RP_AUTH = { ...window.RP_AUTH, session };
  if (window.RP_ACCOUNT) window.RP_ACCOUNT = { ...window.RP_ACCOUNT, session };
  return session;
}

async function currentAuthSession(options = {}) {
  if (typeof window === "undefined") return null;
  const getter = window.RP_GET_AUTH_SESSION;
  if (typeof getter === "function") return getter(options);
  return (window.RP_AUTH && window.RP_AUTH.session) || null;
}

function timeoutErrorMessage() {
  return "Network request timed out. Check your connection and try again.";
}

async function fetchWithTimeout(resource, options = {}, timeoutMs = API_REQUEST_TIMEOUT_MS) {
  const existingSignal = options.signal;
  const controller = new AbortController();
  let timeoutId = null;
  let abortListener = null;

  if (existingSignal?.aborted) {
    controller.abort(existingSignal.reason);
  } else if (existingSignal) {
    abortListener = () => controller.abort(existingSignal.reason);
    existingSignal.addEventListener("abort", abortListener, { once: true });
  }

  timeoutId = window.setTimeout(() => controller.abort(new Error(timeoutErrorMessage())), Math.max(1000, timeoutMs));

  try {
    return await fetch(resource, { ...options, signal: controller.signal });
  } catch (error) {
    if (controller.signal.aborted && !existingSignal?.aborted) {
      throw Object.assign(new Error(timeoutErrorMessage()), { code: "RP_REQUEST_TIMEOUT" });
    }
    throw error;
  } finally {
    if (timeoutId) window.clearTimeout(timeoutId);
    if (existingSignal && abortListener) existingSignal.removeEventListener("abort", abortListener);
  }
}

async function jsonPayload(res) {
  const text = await res.text().catch(() => "");
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    const summary = text.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    const cloudflareHostError = res.status >= 500 && /\b(cloudflare|bad gateway|host error)\b/i.test(summary);
    if (cloudflareHostError) {
      return {
        error: `Operations API returned ${res.status}. Please retry in a moment; an urgent support ticket can include the server details.`,
        detail: summary.slice(0, 1000)
      };
    }
    return summary ? { error: summary.slice(0, 500), detail: summary.slice(0, 1000) } : {};
  }
}

function isAuthRetryable(status, payload) {
  if (status !== 401) return false;
  const message = String(payload?.error || payload?.message || "").toLowerCase();
  return !message || message.includes("expired") || message.includes("invalid") || message.includes("missing bearer");
}

function notifyApiError(path, message, payload, res, options = {}) {
  if (typeof window === "undefined") return;
  if (String(path || "").includes("/api/support/ticket")) return;
  const text = String(message || "").trim();
  if (!text) return;
  const entityScope = supportTicketEntityScope(path, options.body);
  const expected = isExpectedHttpFailure(res.status);
  window.dispatchEvent(new CustomEvent("rp-toast", {
    detail: {
      msg: text,
      tone: expected ? "warn" : "danger",
      ...(expected ? {} : { errorTicket: {
        automatic: true,
        error: text,
        errorCode: payload?.code || "",
        errorSummary: text,
        httpMethod: options.method || "GET",
        httpStatus: res.status,
        requestPath: path,
        ...entityScope,
        title: `API error ${res.status}: ${path}`,
        context: {
          code: payload?.code || "",
          ...entityScope,
          method: options.method || "GET",
          path,
          status: res.status,
          response: payload?.message || payload?.detail || payload?.hint || ""
        },
        source: "Automatic error report - API request"
      } })
    }
  }));
}

export function resolveInitialMode() {
  if (typeof window === "undefined") return "operations";

  const explicit = window.RP_DEFAULT_MODE || new URLSearchParams(window.location.search).get("mode");
  if (["operations", "field", "client"].includes(explicit)) return explicit;

  const host = window.location.hostname.toLowerCase();
  const path = window.location.pathname.toLowerCase();

  if (host.startsWith("sales.") || path.startsWith("/sales")) return "field";
  if (host.startsWith("order.") || path.startsWith("/order")) return "client";
  return "operations";
}

export function isModeLocked() {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname.toLowerCase();
  return host.startsWith("operations.") || host.startsWith("sales.") || host.startsWith("order.");
}

export function getInitialRole(kind) {
  const explicitRole = typeof window !== "undefined"
    ? normalizeRole(new URLSearchParams(window.location.search).get("role"))
    : "";
  const role = normalizeRole(window.RP_AUTH && window.RP_AUTH.profile && window.RP_AUTH.profile.role);

  if (kind === "field") {
    if (["rep", "warehouse", "dispatch"].includes(explicitRole)) return explicitRole;
    if (role === "warehouse") return "warehouse";
    if (role === "dispatcher" || role === "dispatch") return "dispatch";
    return "rep";
  }

  if (["ops", "leadership"].includes(explicitRole)) return explicitRole;
  if (role === "leadership" || role === "founder") return "leadership";
  return "ops";
}

export async function rpApi(path, options = {}) {
  const { retryAuth = true, timeoutMs = API_REQUEST_TIMEOUT_MS, ...fetchOptions } = options;
  const request = async forceRefresh => {
    const headers = new Headers(fetchOptions.headers || {});
    const session = await currentAuthSession({ forceRefresh });
    const token = session && session.access_token;

    if (token) headers.set("Authorization", `Bearer ${token}`);
    if (!headers.has("Authorization") && typeof window.RP_GET_AUTH_SESSION === "function") {
      throw new Error(SESSION_ENDED_MESSAGE);
    }
    if (!headers.has("X-Ranch-Power-Portal")) headers.set("X-Ranch-Power-Portal", resolveInitialMode());
    const isFormData = typeof FormData !== "undefined" && fetchOptions.body instanceof FormData;
    if (fetchOptions.body && !isFormData && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");

    const res = await fetchWithTimeout(path, {
      ...fetchOptions,
      headers,
      credentials: "same-origin"
    }, timeoutMs).catch(error => { throw explainNetworkFailure(error, fetchOptions.method); });
    const payload = await jsonPayload(res);
    return { payload, res };
  };

  let { payload, res } = await request(false);
  if (!res.ok && retryAuth && isAuthRetryable(res.status, payload)) {
    ({ payload, res } = await request(true));
  }

  if (!res.ok) {
    const message = explainHttpFailure({ ...payload, status: res.status, method: fetchOptions.method });
    notifyApiError(path, message, payload, res, fetchOptions);
    const error = new Error(message);
    error.code = payload.code || "";
    error.payload = payload;
    error.status = res.status;
    throw error;
  }

  return payload;
}

function isLocalhost() {
  return typeof window !== "undefined" && LOCAL_HOSTS.has(window.location.hostname);
}

function searchParams() {
  if (typeof window === "undefined") return new URLSearchParams();
  return new URLSearchParams(window.location.search);
}

function developerDataRequested() {
  return developerDataExplicitlyRequested() || developerDataStored();
}

function developerDataExplicitlyRequested() {
  const params = searchParams();
  return ["devData", "developerData", "demoData"].some(key => params.get(key) === "1" || params.get(key) === "true");
}

export function developerDataStored() {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(DEVELOPER_DATA_PREF_KEY) === "1"
      || window.sessionStorage.getItem(DEVELOPER_DATA_PREF_KEY) === "1";
  } catch {
    return false;
  }
}

export function setDeveloperDataPreference(enabled) {
  if (typeof window === "undefined") return false;
  try {
    if (enabled) {
      window.localStorage.setItem(DEVELOPER_DATA_PREF_KEY, "1");
    } else {
      window.localStorage.removeItem(DEVELOPER_DATA_PREF_KEY);
      window.sessionStorage.removeItem(DEVELOPER_DATA_PREF_KEY);
    }
    return true;
  } catch {
    return false;
  }
}

function developerDataAllowedWithoutAuth(config) {
  if (!config) return false;
  if (config.demoMode === true) return true;
  return isLocalhost() && developerDataExplicitlyRequested();
}

function canUseDeveloperData(profile) {
  const email = String(profile?.email || "").trim().toLowerCase();
  const role = normalizeRole(profile?.role);
  return email === OWNER_ADMIN_EMAIL || role === "admin";
}

function demoRoleForMode(profile) {
  const role = normalizeRole(profile?.role);
  if (role) return role;
  const mode = resolveInitialMode();
  if (mode === "client") return "customer";
  if (mode === "field") return "rep";
  return "ops";
}

function buildDeveloperDataAuth(profile = {}, session = null, auth = null) {
  const email = String(profile.email || "developer@ranchpower.com");
  const displayName = String(profile.display_name || nameFromEmail(email) || "Developer Data");
  return {
    demo: true,
    developerData: true,
    session,
    auth,
    liveProfile: profile && profile.id ? profile : null,
    profile: {
      ...profile,
      email,
      display_name: displayName,
      role: demoRoleForMode(profile),
      active: true
    }
  };
}

function publishDeveloperDataHelpers() {
  if (typeof window === "undefined") return;
  window.RP_DEVELOPER_DATA_PREF_KEY = DEVELOPER_DATA_PREF_KEY;
  window.RP_DEVELOPER_DATA_ENABLED = developerDataStored;
  window.RP_SET_DEVELOPER_DATA_PREFERENCE = setDeveloperDataPreference;
  window.RP_CAN_USE_DEVELOPER_DATA = () => {
    const auth = window.RP_AUTH || {};
    return canUseDeveloperData(auth.liveProfile || auth.profile || {});
  };
}

async function loadRuntimeConfig() {
  try {
    const res = await fetchWithTimeout("/api/runtime", { credentials: "same-origin" }, AUTH_REQUEST_TIMEOUT_MS);
    if (!res.ok) throw new Error("Runtime endpoint unavailable");
    return await res.json();
  } catch (error) {
    return {
      demoMode: isLocalhost(),
      developerMode: isLocalhost(),
      authProvider: "local",
      supabaseUrl: "",
      supabaseAnonKey: "",
      warning: error.message
    };
  }
}

function getAccessToken(session) {
  return session && session.access_token ? session.access_token : "";
}

function isInvalidSessionError(error) {
  const status = Number(error?.status || 0);
  const code = String(error?.code || "").toLowerCase();
  const message = String(error?.message || "").toLowerCase();
  return status === 401
    || /(?:invalid|expired|missing|revoked|not_found).*?(?:refresh|session|token)|(?:refresh|session|token).*?(?:invalid|expired|missing|revoked|not_found)/.test(`${code} ${message}`);
}

function workspaceFailureMessage(error) {
  if (error?.status) return explainHttpFailure({ status: error.status, code: error.code, error: error.message });
  if (error?.code === 'RP_REQUEST_TIMEOUT' || error instanceof TypeError) return explainNetworkFailure(error).message;
  return 'We could not load your workspace. Please try again in a moment.';
}

async function fetchProfile(session) {
  const token = getAccessToken(session);
  const headers = new Headers();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  headers.set("X-Ranch-Power-Portal", resolveInitialMode());

  const res = await fetchWithTimeout("/api/me", {
    headers,
    credentials: "same-origin"
  }, AUTH_REQUEST_TIMEOUT_MS);

  if (!res.ok) {
    const payload = await jsonPayload(res);
    const redirect = payload.redirectTo ? ` Use ${payload.redirectTo}.` : "";
    const guidance = explainHttpFailure({ ...payload, status: res.status });
    throw Object.assign(new Error(`${guidance}${redirect}`), { status: res.status, code: payload.code || '' });
  }

  const payload = await res.json();
  return {
    ...payload,
    profile: normalizeProfile(payload.profile)
  };
}

function mutateArray(target, next) {
  if (!Array.isArray(target) || !Array.isArray(next)) return false;
  target.splice(0, target.length, ...next);
  return true;
}

const INVENTORY_PIPELINE_BOOTSTRAP_KEYS = [
  "LOTS",
  "INVENTORY_PROVENANCE",
  "SUPPLIERS",
  "MATERIALS",
  "MATERIAL_LEDGER",
  "PROCUREMENT_DOCUMENTS",
  "PURCHASE_ORDERS",
  "PRODUCTION",
  "PRODUCTION_DOCUMENTS",
  "CAN_PRODUCTION_RUNS",
  "PACK_RECIPES",
  "PACKAGING_RUNS",
  "PRINTED_LOT_METADATA",
  "HISTORICAL_PACKING_CONFIRMATIONS",
  "CAN_SOURCE_RECONCILIATION_HOLDS",
  "LOGISTICS_MOVEMENTS",
  "CHEP_MOVEMENTS",
  "CHEP_BALANCES"
];

function emptyInventoryPipelineBootstrapData() {
  return Object.fromEntries(INVENTORY_PIPELINE_BOOTSTRAP_KEYS.map(key => [key, []]));
}

function applyBootstrapData(payload) {
  if (!payload || !payload.data || !window.RP) return payload;

  if (!Array.isArray(window.RP.USER_PROFILES)) window.RP.USER_PROFILES = [];
  if (!Array.isArray(window.RP.DISPATCH_MANIFESTS)) window.RP.DISPATCH_MANIFESTS = [];
  if (!Array.isArray(window.RP.SUPPLIERS)) window.RP.SUPPLIERS = [];
  if (!Array.isArray(window.RP.MATERIALS)) window.RP.MATERIALS = [];
  if (!Array.isArray(window.RP.MATERIAL_LEDGER)) window.RP.MATERIAL_LEDGER = [];
  if (!Array.isArray(window.RP.PROCUREMENT_DOCUMENTS)) window.RP.PROCUREMENT_DOCUMENTS = [];
  if (!Array.isArray(window.RP.PURCHASE_ORDERS)) window.RP.PURCHASE_ORDERS = [];
  if (!Array.isArray(window.RP.PRODUCTION)) window.RP.PRODUCTION = [];
  if (!Array.isArray(window.RP.PRODUCTION_DOCUMENTS)) window.RP.PRODUCTION_DOCUMENTS = [];
  if (!Array.isArray(window.RP.CAN_PRODUCTION_RUNS)) window.RP.CAN_PRODUCTION_RUNS = [];
  if (!Array.isArray(window.RP.PACK_RECIPES)) window.RP.PACK_RECIPES = [];
  if (!Array.isArray(window.RP.PACKAGING_RUNS)) window.RP.PACKAGING_RUNS = [];
  if (!Array.isArray(window.RP.PRINTED_LOT_METADATA)) window.RP.PRINTED_LOT_METADATA = [];
  if (!Array.isArray(window.RP.HISTORICAL_PACKING_CONFIRMATIONS)) window.RP.HISTORICAL_PACKING_CONFIRMATIONS = [];
  if (!Array.isArray(window.RP.CAN_SOURCE_RECONCILIATION_HOLDS)) window.RP.CAN_SOURCE_RECONCILIATION_HOLDS = [];
  if (!Array.isArray(window.RP.LOGISTICS_MOVEMENTS)) window.RP.LOGISTICS_MOVEMENTS = [];
  if (!Array.isArray(window.RP.CHEP_MOVEMENTS)) window.RP.CHEP_MOVEMENTS = [];
  if (!Array.isArray(window.RP.CHEP_BALANCES)) window.RP.CHEP_BALANCES = [];
  if (!Array.isArray(window.RP.REORDER_ALERTS)) window.RP.REORDER_ALERTS = [];
  if (!Array.isArray(window.RP.TAX_RULES)) window.RP.TAX_RULES = [];
  if (!Array.isArray(window.RP.TERRITORIES)) window.RP.TERRITORIES = [];
  if (!Array.isArray(window.RP.TERRITORY_RECORDS)) window.RP.TERRITORY_RECORDS = [];
  if (!Array.isArray(window.RP.SALES_GOALS)) window.RP.SALES_GOALS = [];
  if (!Array.isArray(window.RP.PORTAL_AVAILABILITY)) window.RP.PORTAL_AVAILABILITY = [];
  if (!Array.isArray(window.RP.PORTAL_FULFILLMENT)) window.RP.PORTAL_FULFILLMENT = [];
  if (!Array.isArray(window.RP.PORTAL_PRICE_MAP)) window.RP.PORTAL_PRICE_MAP = [];
  if (!Array.isArray(window.RP.FIELD_RECEIPTS)) window.RP.FIELD_RECEIPTS = [];
  if (!Array.isArray(window.RP.EVENTS)) window.RP.EVENTS = [];
  if (!Array.isArray(window.RP.EVENT_TEAM)) window.RP.EVENT_TEAM = [];
  if (!Array.isArray(window.RP.EVENT_INVENTORY_PLAN)) window.RP.EVENT_INVENTORY_PLAN = [];
  if (!Array.isArray(window.RP.EVENT_INVENTORY_ALLOCATIONS)) window.RP.EVENT_INVENTORY_ALLOCATIONS = [];
  if (!Array.isArray(window.RP.EVENT_SALES_LINKS)) window.RP.EVENT_SALES_LINKS = [];
  if (!Array.isArray(window.RP.EVENT_CLOSEOUTS)) window.RP.EVENT_CLOSEOUTS = [];
  if (!Array.isArray(window.RP.EVENT_INVENTORY_CLOSEOUT_RECEIPTS)) window.RP.EVENT_INVENTORY_CLOSEOUT_RECEIPTS = [];
  if (!Array.isArray(window.RP.EVENT_INVENTORY_OPEN_CASE_RECEIPTS)) window.RP.EVENT_INVENTORY_OPEN_CASE_RECEIPTS = [];
  if (!Array.isArray(window.RP.OPEN_CASE_BATCHES)) window.RP.OPEN_CASE_BATCHES = [];
  if (!Array.isArray(window.RP.OPEN_CASE_MOVEMENTS)) window.RP.OPEN_CASE_MOVEMENTS = [];
  if (!Array.isArray(window.RP.LOTS)) window.RP.LOTS = [];
  if (!Array.isArray(window.RP.INVENTORY_PROVENANCE)) window.RP.INVENTORY_PROVENANCE = [];
  if (!Array.isArray(window.RP.ORDER_STOCK_COMMITMENTS)) window.RP.ORDER_STOCK_COMMITMENTS = [];

  const keys = ["SKUS", "PRICE_LISTS", "TERRITORY_RECORDS", "TERRITORIES", "TAX_RULES", "USER_PROFILES", "CUSTOMERS", "LOCATIONS", "LOTS", "INVENTORY_PROVENANCE", "ORDERS", "ORDER_STOCK_COMMITMENTS", "INV", "LEDGER", "PORTAL_AVAILABILITY", "PORTAL_FULFILLMENT", "PORTAL_PRICE_MAP", "DEDUCTIONS", "EXPENSES", "VISITS", "TASKS", "LEADS", "SALES_GOALS", "SUPPLIERS", "MATERIALS", "MATERIAL_LEDGER", "PROCUREMENT_DOCUMENTS", "PURCHASE_ORDERS", "PRODUCTION", "PRODUCTION_DOCUMENTS", "CAN_PRODUCTION_RUNS", "PACK_RECIPES", "PACKAGING_RUNS", "PRINTED_LOT_METADATA", "HISTORICAL_PACKING_CONFIRMATIONS", "CAN_SOURCE_RECONCILIATION_HOLDS", "LOGISTICS_MOVEMENTS", "CHEP_MOVEMENTS", "CHEP_BALANCES", "REORDER_ALERTS", "LANES", "DISPATCH", "DISPATCH_MANIFESTS", "DISPATCH_LOAD_CHECKS", "STOCK_REQUESTS", "FIELD_RECEIPTS", "APPROVAL_RULES", "EVENTS", "EVENT_TEAM", "EVENT_INVENTORY_PLAN", "EVENT_INVENTORY_ALLOCATIONS", "EVENT_SALES_LINKS", "EVENT_CLOSEOUTS", "EVENT_INVENTORY_CLOSEOUT_RECEIPTS", "EVENT_INVENTORY_OPEN_CASE_RECEIPTS", "OPEN_CASE_BATCHES", "OPEN_CASE_MOVEMENTS", "SHOPIFY_INVENTORY_RECONCILIATIONS", "SHOPIFY_SYNC_RUNS"];
  const applied = keys.filter(key => mutateArray(window.RP[key], payload.data[key]));

  if (applied.length) {
    const previousBootstrap = window.RP_BOOTSTRAP && typeof window.RP_BOOTSTRAP === "object"
      ? window.RP_BOOTSTRAP
      : {};
    window.RP_BOOTSTRAP = {
      dataSource: payload.dataSource,
      applied,
      loadedAt: new Date().toISOString(),
      warnings: payload.warnings || [],
      orderCache: payload.orderCache || previousBootstrap.orderCache || null,
      customerSalesMetrics: payload.customerSalesMetrics || previousBootstrap.customerSalesMetrics || null,
      stockCommitments: payload.stockCommitments || previousBootstrap.stockCommitments || null,
      salesGoalActuals: payload.salesGoalActuals || previousBootstrap.salesGoalActuals || null
    };
  }

  return payload;
}

async function fetchBootstrapScope(headers, scope, signal) {
  const timeoutMs = scope === "inventory-pipeline"
    ? INVENTORY_PIPELINE_BOOTSTRAP_TIMEOUT_MS
    : BOOTSTRAP_REQUEST_TIMEOUT_MS;
  const res = await fetchWithTimeout(`/api/bootstrap?scope=${encodeURIComponent(scope)}`, {
    headers, signal,
    credentials: "same-origin"
  }, timeoutMs);

  const payload = await jsonPayload(res);
  if (!res.ok) {
    throw Object.assign(new Error(explainHttpFailure({ ...payload, status: res.status })), { status: res.status, code: payload.code || '' });
  }

  if (payload.dataSource !== "supabase") {
    throw new Error(payload.message || "Live data bootstrap did not return Supabase data.");
  }

  return payload;
}

async function fetchBootstrap(session, { signal, reason = "initial", apply = true } = {}) {
  const token = getAccessToken(session);
  if (!token) throw new Error("Missing secure session token.");

  const headers = new Headers();
  headers.set("Authorization", `Bearer ${token}`);
  headers.set("X-Ranch-Power-Portal", resolveInitialMode());
  headers.set("X-Ranch-Power-Refresh-Reason", refreshReason(reason));
  const scopeAtStart = refreshScopeKey(window.RP_AUTH, resolveInitialMode());

  const [coreResult, inventoryPipelineResult] = await Promise.allSettled([
    fetchBootstrapScope(headers, "core", signal),
    fetchBootstrapScope(headers, "inventory-pipeline", signal)
  ]);

  if (coreResult.status === "rejected") throw coreResult.reason;

  const corePayload = coreResult.value;
  const pipelineFallback = emptyInventoryPipelineBootstrapData();
  const pipelinePayload = inventoryPipelineResult.status === "fulfilled"
    ? inventoryPipelineResult.value
    : null;
  const pipelineWarning = inventoryPipelineResult.status === "rejected"
    ? `Inventory pipeline data could not be loaded: ${inventoryPipelineResult.reason instanceof Error ? inventoryPipelineResult.reason.message : "Unknown error."}`
    : "";
  const payload = {
    ...corePayload,
    data: {
      ...corePayload.data,
      ...pipelineFallback,
      ...(pipelinePayload?.data || {})
    },
    warnings: [
      ...(Array.isArray(corePayload.warnings) ? corePayload.warnings : []),
      ...(Array.isArray(pipelinePayload?.warnings) ? pipelinePayload.warnings : []),
      ...(pipelineWarning ? [pipelineWarning] : [])
    ]
  };

  if (signal?.aborted || scopeAtStart !== refreshScopeKey(window.RP_AUTH, resolveInitialMode())) {
    throw Object.assign(new Error("Account or access scope changed during refresh."), { name: "AbortError" });
  }
  return apply ? applyBootstrapData(payload) : payload;
}

function modeLabel() {
  const mode = resolveInitialMode();
  if (mode === "field") return "Sales";
  if (mode === "client") return "Ordering";
  return "Operations";
}

function nameFromEmail(email) {
  const local = String(email || "").split("@")[0] || "";
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map(value => value.charAt(0).toUpperCase() + value.slice(1))
    .join(" ");
}

function parseAuthFlow(locationLike) {
  const parts = [
    String(locationLike?.hash || "").replace(/^#/, ""),
    String(locationLike?.search || "").replace(/^\?/, "")
  ].filter(Boolean);
  try {
    const params = new URLSearchParams(parts.join("&"));
    const accountAction = params.get("accountAction") || params.get("flow") || params.get("rp_action") || "";
    const hasToken = params.has("access_token") || params.has("refresh_token") || params.has("code");
    return {
      type: params.get("type") || (hasToken ? accountAction : ""),
      hasToken,
      errorCode: String(params.get("error_code") || "").toLowerCase(),
      hasError: params.has("error") || params.has("error_code")
    };
  } catch {
    return { type: "", hasToken: false, errorCode: "", hasError: false };
  }
}

function authFlowErrorMessage(authFlow) {
  if (!authFlow?.hasError) return "";
  if (authFlow.errorCode === "otp_expired") {
    return OTP_EXPIRED_MESSAGE;
  }
  return "This email sign-in link could not be used. Sign in with your password or request a new magic link.";
}

function authErrorGuidance(error) {
  const status = Number(error?.status || 0);
  const code = String(error?.code || "").toLowerCase();
  if (code === "otp_expired") return OTP_EXPIRED_MESSAGE;
  if (code === "over_email_send_rate_limit") return "Too many sign-in emails were requested. Wait before requesting another link, or sign in with your password.";
  if (status === 429 || code === "over_request_rate_limit") return "Sign-in is temporarily rate limited. Wait a few minutes before trying again, or use your password if available.";
  return "";
}

function guidedAuthError(error) {
  const message = authErrorGuidance(error);
  if (!message) return error;
  return Object.assign(new Error(message, { cause: error }), { status: error?.status, code: error?.code });
}

function customerForProfile(profile) {
  const customerId = String(profile?.customer_id || "").trim();
  if (!customerId || typeof window === "undefined" || !window.RP || !Array.isArray(window.RP.CUSTOMERS)) return null;
  return window.RP.CUSTOMERS.find(customer => String(customer?.id || "") === customerId) || null;
}

function customerNeedsRetailOnboarding(profile, customer) {
  if (String(profile?.role || "").trim().toLowerCase() !== "customer") return false;
  if (!customer) return false;
  if (customer.onboardingSubmittedAt) return false;
  if (String(customer.status || "").trim().toLowerCase() !== "prospect") return false;
  const questionnaire = customer.onboardingQuestionnaire && typeof customer.onboardingQuestionnaire === "object"
    ? customer.onboardingQuestionnaire
    : {};
  return questionnaire.quickQuoteProspect === true;
}

function accountSetupReason(profile, authFlow, customer = null) {
  const setupComplete = Boolean(profile && profile.profile_completed_at);
  const type = String(authFlow?.type || "").toLowerCase();
  if (type === "recovery") return "recovery";
  if (!setupComplete && type === "invite") return "invite";
  if (profile && !setupComplete) return "invite";
  if (profile && !String(profile.display_name || "").trim()) return "profile";
  if (customerNeedsRetailOnboarding(profile, customer)) return "onboarding";
  return "";
}

function accountActionRedirect(action) {
  const url = new URL(window.location.origin);
  url.searchParams.set("accountAction", action);
  return url.toString();
}

function clearAuthFlowFromUrl() {
  if (typeof window === "undefined" || !window.history || !window.location) return;
  const url = new URL(window.location.href);
  ["accountAction", "flow", "rp_action", "type", "token", "token_hash", "access_token", "refresh_token", "expires_at", "expires_in", "code", "error", "error_code", "error_description"].forEach(key => {
    url.searchParams.delete(key);
  });
  const hashParams = new URLSearchParams(String(url.hash || "").replace(/^#/, ""));
  ["accountAction", "flow", "rp_action", "type", "token", "token_hash", "access_token", "refresh_token", "expires_at", "expires_in", "code", "error", "error_code", "error_description"].forEach(key => {
    hashParams.delete(key);
  });
  url.hash = hashParams.toString();
  window.history.replaceState({}, document.title, url.toString());
}

function clearHandledAuthTokensFromUrl(authFlow) {
  if (!authFlow || (!authFlow.hasToken && !authFlow.hasError)) return;
  clearAuthFlowFromUrl();
}

function publishProfileChange(profile) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("rp-auth-profile-change", {
    detail: {
      profile,
      at: new Date().toISOString()
    }
  }));
}

export function PlatformGate({ children }) {
  const refreshCoordinator = useRef(null);
  const authGeneration = useRef(0);
  const passwordSignInPending = useRef(false);
  const [state, setState] = useState({ phase: "loading", message: "Preparing secure workspace..." });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [setupDismissed, setSetupDismissed] = useState(false);
  const initialAuthFlow = useMemo(() => parseAuthFlow(typeof window !== "undefined" ? window.location : null), []);

  const markSessionLost = useCallback(() => {
    authGeneration.current += 1;
    window.RP_AUTH = null;
    window.RP_ACCOUNT = null;
    window.RP_REFRESH_DATA = null;
    setState(prev => {
      if (prev.phase !== "ready" && prev.phase !== "data-error" && prev.phase !== "auth-check"
        && !(prev.phase === "login" && passwordSignInPending.current)) return prev;
      return {
        ...prev,
        phase: "login",
        auth: null,
        profile: null,
        liveProfile: null,
        message: (prev.phase === "auth-check" && authFlowErrorMessage(initialAuthFlow)) || SESSION_ENDED_MESSAGE,
        error: true
      };
    });
  }, [initialAuthFlow]);

  const client = useMemo(() => {
    if (!state.config || !state.config.supabaseUrl || !state.config.supabaseAnonKey) return null;
    return createClient(state.config.supabaseUrl, state.config.supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true
      }
    });
  }, [state.config]);

  const getCurrentSession = useCallback(async ({ forceRefresh = false } = {}) => {
    if (!client) return null;
    const generation = authGeneration.current;

    const current = await client.auth.getSession();
    if (generation !== authGeneration.current) return null;
    if (current.error) {
      if (isInvalidSessionError(current.error)) markSessionLost();
      throw guidedAuthError(current.error);
    }

    let session = current.data.session || null;
    if (!session) {
      markSessionLost();
      return null;
    }

    if (forceRefresh || isSessionNearExpiry(session)) {
      const refreshed = await client.auth.refreshSession();
      if (generation !== authGeneration.current) return null;
      if (refreshed.error) {
        if (isInvalidSessionError(refreshed.error)) markSessionLost();
        throw guidedAuthError(refreshed.error);
      }
      session = refreshed.data.session || null;
      if (!session) {
        markSessionLost();
        return null;
      }
    }

    return publishSession(session);
  }, [client, markSessionLost]);

  useEffect(() => {
    if (!client) return undefined;
    const { data: sub } = client.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") {
        clearHandledAuthTokensFromUrl(initialAuthFlow);
        markSessionLost();
      } else if (session) {
        publishSession(session);
      }
    });
    return () => sub.subscription.unsubscribe();
  }, [client, initialAuthFlow, markSessionLost]);

  useEffect(() => {
    if (typeof window === "undefined" || !client) return undefined;
    window.RP_GET_AUTH_SESSION = getCurrentSession;
    return () => {
      if (window.RP_GET_AUTH_SESSION === getCurrentSession) window.RP_GET_AUTH_SESSION = null;
    };
  }, [client, getCurrentSession]);

  useEffect(() => {
    let active = true;
    publishDeveloperDataHelpers();

    async function boot() {
      const config = await loadRuntimeConfig();
      if (!active) return;

      if (developerDataAllowedWithoutAuth(config)) {
        window.RP_AUTH = buildDeveloperDataAuth();
        setState({ phase: "ready", config, profile: window.RP_AUTH.profile, demo: true, developerData: true });
        return;
      }

      if (!config.supabaseUrl || !config.supabaseAnonKey) {
        setState({
          phase: "setup",
          config,
          message: "Supabase public config is not set for this deployment yet."
        });
        return;
      }

      setState({ phase: "auth-check", config, message: "Checking secure session..." });
    }

    boot();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!client || state.phase !== "auth-check") return;
    let active = true;

    async function loadSession() {
      const generation = authGeneration.current;
      const { data, error } = await client.auth.getSession();
      if (!active || generation !== authGeneration.current) return;

      if (error) {
        clearHandledAuthTokensFromUrl(initialAuthFlow);
        setState(prev => ({
          ...prev,
          phase: "login",
          message: authFlowErrorMessage(initialAuthFlow) || authErrorGuidance(error) || (isInvalidSessionError(error) ? SESSION_ENDED_MESSAGE : error.message),
          error: true
        }));
        return;
      }

      if (!data.session) {
        clearHandledAuthTokensFromUrl(initialAuthFlow);
        setState(prev => ({ ...prev, phase: "login", message: authFlowErrorMessage(initialAuthFlow), error: initialAuthFlow.hasError }));
        return;
      }

      try {
        const session = await getCurrentSession({ forceRefresh: isSessionNearExpiry(data.session) });
        if (!active || generation !== authGeneration.current) return;
        if (!session) {
          clearHandledAuthTokensFromUrl(initialAuthFlow);
          setState(prev => ({ ...prev, phase: "login", message: authFlowErrorMessage(initialAuthFlow), error: initialAuthFlow.hasError }));
          return;
        }
        const me = await fetchProfile(session);
        if (!active || generation !== authGeneration.current) return;
        clearHandledAuthTokensFromUrl(initialAuthFlow);
        if (developerDataRequested() && canUseDeveloperData(me.profile)) {
          window.RP_AUTH = buildDeveloperDataAuth(me.profile, session, me.auth);
          setState(prev => ({
            ...prev,
            auth: me.auth,
            phase: "ready",
            profile: window.RP_AUTH.profile,
            liveProfile: me.profile,
            demo: true,
            developerData: true,
            error: false
          }));
          return;
        }
        if (developerDataRequested()) setDeveloperDataPreference(false);
        window.RP_AUTH = { auth: me.auth, session, profile: me.profile };
        try {
          await fetchBootstrap(session);
        } catch (error) {
          if (!active || generation !== authGeneration.current) return;
          if (isInvalidSessionError(error)) {
            markSessionLost();
            return;
          }
          setState(prev => ({
            ...prev,
            phase: "data-error",
            auth: me.auth,
            profile: me.profile,
            message: workspaceFailureMessage(error),
            status: error?.status || null,
            error: true
          }));
          return;
        }
        if (!active || generation !== authGeneration.current) return;
        setState(prev => ({ ...prev, auth: me.auth, phase: "ready", profile: me.profile }));
      } catch (error) {
        if (!active || generation !== authGeneration.current) return;
        if (!isInvalidSessionError(error) && Number(error?.status || 0) !== 403) {
          setState(prev => ({ ...prev, phase: 'data-error', message: workspaceFailureMessage(error), status: error?.status || null, error: true }));
          return;
        }
        clearHandledAuthTokensFromUrl(initialAuthFlow);
        await client.auth.signOut();
        setState(prev => ({
          ...prev,
          phase: "login",
          message: authErrorGuidance(error) || (isInvalidSessionError(error) ? SESSION_ENDED_MESSAGE : error.message),
          error: true
        }));
      }
    }

    loadSession();

    return () => {
      active = false;
    };
  }, [client, getCurrentSession, initialAuthFlow, state.phase]);

  async function signInWithPassword(event) {
    event.preventDefault();
    if (!client || busy) return;
    const generation = authGeneration.current;
    passwordSignInPending.current = true;
    setBusy(true);
    setState(prev => ({ ...prev, message: "Signing in..." }));

    const { data, error } = await client.auth.signInWithPassword({ email, password });
    if (generation !== authGeneration.current) {
      passwordSignInPending.current = false;
      setBusy(false);
      return;
    }
    if (error) {
      passwordSignInPending.current = false;
      setBusy(false);
      setState(prev => ({ ...prev, phase: "login", message: authErrorGuidance(error) || error.message, error: true }));
      return;
    }

    try {
      const session = await getCurrentSession({ forceRefresh: isSessionNearExpiry(data.session) });
      if (generation !== authGeneration.current) return;
      if (!session) throw new Error("Secure sign-in did not return a session.");
      const me = await fetchProfile(session);
      if (generation !== authGeneration.current) return;
      clearHandledAuthTokensFromUrl(initialAuthFlow);
      if (developerDataRequested() && canUseDeveloperData(me.profile)) {
        window.RP_AUTH = buildDeveloperDataAuth(me.profile, session, me.auth);
        setState(prev => ({
          ...prev,
          auth: me.auth,
          phase: "ready",
          profile: window.RP_AUTH.profile,
          liveProfile: me.profile,
          demo: true,
          developerData: true,
          error: false
        }));
        return;
      }
      if (developerDataRequested()) setDeveloperDataPreference(false);
      window.RP_AUTH = { auth: me.auth, session, profile: me.profile };
      try {
        await fetchBootstrap(session);
      } catch (error) {
        if (generation !== authGeneration.current) return;
        if (isInvalidSessionError(error)) {
          markSessionLost();
          return;
        }
        setState(prev => ({
          ...prev,
          phase: "data-error",
          auth: me.auth,
          profile: me.profile,
          message: workspaceFailureMessage(error),
          status: error?.status || null,
          error: true
        }));
        return;
      }
      if (generation !== authGeneration.current) return;
      setState(prev => ({ ...prev, auth: me.auth, phase: "ready", profile: me.profile, error: false }));
    } catch (error) {
      if (generation !== authGeneration.current) return;
      if (!isInvalidSessionError(error) && Number(error?.status || 0) !== 403) {
        setState(prev => ({ ...prev, phase: 'data-error', message: workspaceFailureMessage(error), status: error?.status || null, error: true }));
        return;
      }
      clearHandledAuthTokensFromUrl(initialAuthFlow);
      await client.auth.signOut();
      setState(prev => ({
        ...prev,
        phase: "login",
        message: authErrorGuidance(error) || (isInvalidSessionError(error) ? SESSION_ENDED_MESSAGE : error.message),
        error: true
      }));
    } finally {
      passwordSignInPending.current = false;
      setBusy(false);
    }
  }

  async function sendMagicLinkEmail(targetEmail) {
    const emailValue = String(targetEmail || "").trim();
    if (!client) throw new Error("Secure account actions are not available in this workspace.");
    if (!emailValue) throw new Error("Enter an email address first.");
    const { error } = await client.auth.signInWithOtp({
      email: emailValue,
      options: {
        emailRedirectTo: window.location.origin,
        shouldCreateUser: false
      }
    });
    if (error) throw guidedAuthError(error);
    return { ok: true, email: emailValue };
  }

  async function sendMagicLink() {
    if (!client || busy || !email) return;
    setBusy(true);
    let message = "Check your email for a secure sign-in link.";
    let errorState = false;
    try {
      await sendMagicLinkEmail(email);
    } catch (error) {
      message = error instanceof Error ? error.message : "Could not send magic link.";
      errorState = true;
    }
    setBusy(false);
    setState(prev => ({
      ...prev,
      phase: "login",
      message,
      error: errorState
    }));
  }

  async function signOut() {
    authGeneration.current += 1;
    if (client) await client.auth.signOut();
    window.RP_AUTH = null;
    window.RP_ACCOUNT = null;
    window.RP_REFRESH_DATA = null;
    setState(prev => ({
      ...prev,
      phase: "login",
      auth: null,
      profile: null,
      liveProfile: null,
      demo: false,
      message: "",
      error: false
    }));
  }

  async function refreshData({ reason = "manual" } = {}) {
    if (readEfficiencyEnabled(window.RP_AUTH?.profile) && refreshCoordinator.current) {
      const payload = await refreshCoordinator.current.request({ reason });
      return { ...payload, refreshEventPublished: true };
    }
    const session = await getCurrentSession();
    if (!session) throw new Error("Missing secure session token.");

    const payload = await fetchBootstrap(session, { reason });
    setState(prev => ({ ...prev, lastDataRefreshAt: new Date().toISOString(), error: false }));
    return payload;
  }

  useEffect(() => {
    if (!client || state.phase !== "ready" || state.demo) return;
    if (readEfficiencyEnabled(state.profile)) {
      const coordinator = createRefreshCoordinator({
        getScopeKey: () => refreshScopeKey(window.RP_AUTH, resolveInitialMode()),
        isVisible: () => document.visibilityState !== "hidden" && !isActiveTextEntry(),
        read: async ({ signal, reason }) => {
          const session = await getCurrentSession();
          if (!session) throw Object.assign(new Error("Missing secure session token."), { status: 401 });
          return fetchBootstrap(session, { signal, reason, apply: false });
        },
        apply: applyBootstrapData,
        onSuccess: (payload, { source }) => {
          setState(prev => ({ ...prev, lastDataRefreshAt: new Date().toISOString(), error: false }));
          window.dispatchEvent(new CustomEvent("rp-data-refresh", {
            detail: { source, status: "ok", dataSource: payload.dataSource, at: new Date().toISOString() }
          }));
        },
        onStatus: detail => window.dispatchEvent(new CustomEvent("rp-refresh-status", { detail }))
      });
      refreshCoordinator.current = coordinator;
      let channel = null;
      let disposed = false;
      let subscribedBefore = false;
      let disconnecting = Promise.resolve();
      let connecting = false;
      const connect = async () => {
        if (disposed || channel || connecting || document.visibilityState === "hidden") return;
        connecting = true;
        await disconnecting;
        connecting = false;
        if (disposed || channel || document.visibilityState === "hidden") return;
        channel = client.channel("ranch-power-live-data");
        const subscribedChannel = channel;
        REALTIME_TABLES.forEach(table => channel.on("postgres_changes", { event: "*", schema: "public", table }, () => coordinator.invalidate("realtime")));
        channel.subscribe(status => {
          if (disposed || channel !== subscribedChannel) return;
          if (status === "SUBSCRIBED") {
            if (subscribedBefore) coordinator.invalidate("reconnect");
            subscribedBefore = true;
          } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED") {
            window.dispatchEvent(new CustomEvent("rp-refresh-status", { detail: { status: "error", source: "realtime" } }));
          }
        });
      };
      const onVisibility = () => {
        coordinator.visibilityChanged();
        if (document.visibilityState === "hidden") {
          const previous = channel;
          channel = null;
          if (previous) disconnecting = client.removeChannel(previous).catch(() => {});
        } else connect();
      };
      const onOnline = () => { coordinator.invalidate("reconnect"); connect(); };
      const onFocusOut = () => window.setTimeout(() => coordinator.reconcile("focus"), 0);
      document.addEventListener("visibilitychange", onVisibility);
      document.addEventListener("focusout", onFocusOut);
      window.addEventListener("online", onOnline);
      connect();
      return () => {
        disposed = true;
        coordinator.dispose();
        if (refreshCoordinator.current === coordinator) refreshCoordinator.current = null;
        document.removeEventListener("visibilitychange", onVisibility);
        document.removeEventListener("focusout", onFocusOut);
        window.removeEventListener("online", onOnline);
        if (channel) void client.removeChannel(channel);
      };
    }
    let active = true;
    let timer = null;
    let refreshing = false;
    let queued = false;
    const canBackgroundRefresh = () => document.visibilityState !== "hidden" && !isActiveTextEntry();

    const publish = (status, detail = {}) => {
      if (typeof window === "undefined") return;
      window.dispatchEvent(new CustomEvent(status === "ok" ? "rp-data-refresh" : "rp-refresh-status", {
        detail: {
          source: "realtime",
          status,
          at: new Date().toISOString(),
          ...detail
        }
      }));
    };

    const runRefresh = async () => {
      if (!active) return;
      if (!canBackgroundRefresh()) { queued = true; return; }
      if (refreshing) {
        queued = true;
        return;
      }
      queued = false;
      refreshing = true;
      try {
        const session = await getCurrentSession();
        if (!session) return;
        const payload = await fetchBootstrap(session, { reason: "realtime", apply: false });
        if (!active) return;
        if (!canBackgroundRefresh()) { queued = true; return; }
        applyBootstrapData(payload);
        setState(prev => ({ ...prev, lastDataRefreshAt: new Date().toISOString(), error: false }));
        publish("ok", { dataSource: payload.dataSource });
      } catch (error) {
        console.warn("Ranch Power realtime refresh failed.", error);
        publish("error", { message: error instanceof Error ? error.message : "Realtime refresh failed." });
      } finally {
        refreshing = false;
        if (queued && canBackgroundRefresh()) scheduleRefresh();
      }
    };

    const scheduleRefresh = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(runRefresh, REALTIME_REFRESH_DEBOUNCE_MS);
    };

    const channel = client.channel("ranch-power-live-data");
    const onFocusOut = () => { if (queued) window.setTimeout(scheduleRefresh, 0); };
    const onVisible = () => { if (queued && document.visibilityState !== "hidden") scheduleRefresh(); };
    document.addEventListener("focusout", onFocusOut);
    document.addEventListener("visibilitychange", onVisible);
    REALTIME_TABLES.forEach(table => {
      channel.on("postgres_changes", { event: "*", schema: "public", table }, scheduleRefresh);
    });
    channel.subscribe(status => {
      if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") publish("error", { message: `Realtime channel ${status.toLowerCase().replace("_", " ")}.` });
    });

    return () => {
      active = false;
      if (timer) clearTimeout(timer);
      document.removeEventListener("focusout", onFocusOut);
      document.removeEventListener("visibilitychange", onVisible);
      client.removeChannel(channel);
    };
  }, [client, getCurrentSession, state.demo, state.phase, state.profile]);

  useEffect(() => {
    if (!client || state.phase !== "ready" || state.demo) return undefined;
    let active = true;

    const recoverSession = async (forceRefresh = false) => {
      try {
        const session = await getCurrentSession({ forceRefresh });
        if (!active || !session) return;
        window.dispatchEvent(new CustomEvent("rp-auth-session-refresh", {
          detail: { at: new Date().toISOString(), forceRefresh }
        }));
      } catch (error) {
        console.warn("Ranch Power session recovery failed.", error);
      }
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") recoverSession(false);
    };
    const onPageShow = event => recoverSession(Boolean(event.persisted));
    const onOnline = () => recoverSession(false);

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("online", onOnline);
    window.RP_RECOVER_SESSION = recoverSession;

    return () => {
      active = false;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("online", onOnline);
      if (window.RP_RECOVER_SESSION === recoverSession) window.RP_RECOVER_SESSION = null;
    };
  }, [client, getCurrentSession, state.demo, state.phase]);

  async function updatePassword(nextPassword) {
    const passwordValue = String(nextPassword || "");
    if (!client) throw new Error("Secure account actions are not available in this workspace.");
    if (passwordValue.length < 12) throw new Error("Use at least 12 characters for your password.");

    const { error } = await client.auth.updateUser({ password: passwordValue });
    if (error) throw error;

    setState(prev => ({ ...prev, message: "Password updated.", error: false }));
    return { ok: true };
  }

  async function sendPasswordResetEmail(targetEmail) {
    const profile = state.profile || {};
    const emailValue = String(targetEmail || profile.email || "").trim();
    if (!client) throw new Error("Secure account actions are not available in this workspace.");
    if (!emailValue) throw new Error("This account does not have an email address.");

    const { error } = await client.auth.resetPasswordForEmail(emailValue, {
      redirectTo: accountActionRedirect("recovery")
    });
    if (error) throw error;

    return { ok: true, email: emailValue };
  }

  async function updateProfile(patch) {
    const payload = await rpApi("/api/profile/update", {
      method: "POST",
      body: JSON.stringify(patch || {})
    });
    const nextProfile = payload.profile;
    window.RP_AUTH = { ...(window.RP_AUTH || {}), profile: nextProfile };
    window.RP_ACCOUNT = window.RP_ACCOUNT ? { ...window.RP_ACCOUNT, profile: nextProfile } : window.RP_ACCOUNT;
    publishProfileChange(nextProfile);
    setState(prev => ({ ...prev, profile: nextProfile, message: "Profile updated.", error: false }));
    return payload;
  }

  if (state.phase === "ready") {
    window.RP_SIGN_OUT = signOut;
    window.RP_REFRESH_DATA = refreshData;
    window.RP_ACCOUNT = {
      auth: state.auth,
      profile: state.profile,
      liveProfile: state.liveProfile,
      developerData: state.developerData === true,
      signOut,
      updatePassword,
      sendMagicLinkEmail,
      sendPasswordResetEmail,
      updateProfile,
      refreshData
    };
    const setupCustomer = customerForProfile(state.profile);
    const setupReason = accountSetupReason(state.profile, initialAuthFlow, setupCustomer);
    const showSetup = !state.demo && !setupDismissed && setupReason;
    return (
      <>
        {children}
        {showSetup && (
          <AccountSetupOverlay
            flow={initialAuthFlow}
            onClose={() => setSetupDismissed(true)}
            customer={setupCustomer}
            profile={state.profile}
            reason={setupReason}
            sendPasswordResetEmail={sendPasswordResetEmail}
            updatePassword={updatePassword}
            updateProfile={updateProfile}
          />
        )}
        {state.demo && <div className="rp-developer-data-banner">Developer data mode</div>}
      </>
    );
  }

  if (state.phase === "loading" || state.phase === "auth-check") {
    return <AuthFrame message={state.message || "Loading..."} />;
  }

  if (state.phase === "setup") {
    return (
      <AuthFrame
        title="Secure Setup Needed"
        message={state.message}
        detail="Add Supabase project variables to the Cloudflare Pages project before exposing this subdomain."
      />
    );
  }

  if (state.phase === "data-error") {
    return (
      <AuthFrame
        title="Workspace did not load"
        message={state.message}
        error
        detail="Your sign-in is still active. Please try loading the workspace again."
      >
        <button type="button" onClick={() => setState(prev => ({ ...prev, phase: 'auth-check', message: 'Loading your workspace...', error: false }))}>Try again</button>
        <button className="secondary" type="button" onClick={signOut}>Sign out</button>
        {state.status && <details><summary>Details for support</summary><p>Server response: {state.status}</p></details>}
      </AuthFrame>
    );
  }

  return (
    <AuthFrame title={`${modeLabel()} Sign In`} message={state.message} error={state.error}>
      <form onSubmit={signInWithPassword}>
        <label htmlFor="rp-email">Email</label>
        <input
          id="rp-email"
          autoComplete="email"
          inputMode="email"
          required
          value={email}
          onChange={event => setEmail(event.target.value)}
          placeholder="name@ranchpower.com"
          type="email"
        />
        <label htmlFor="rp-password">Password</label>
        <input
          id="rp-password"
          autoComplete="current-password"
          required
          value={password}
          onChange={event => setPassword(event.target.value)}
          placeholder="Password"
          type="password"
        />
        <button disabled={busy} type="submit">Sign In</button>
        <button className="secondary" disabled={busy || !email} type="button" onClick={sendMagicLink}>
          Send Magic Link
        </button>
      </form>
    </AuthFrame>
  );
}

function onboardingInitialForm(customer) {
  const existing = customer?.onboardingQuestionnaire || {};
  const info = existing.businessInformation || {};
  const contacts = existing.businessContacts || {};
  const delivery = [customer?.address, customer?.city, customer?.postal].filter(Boolean).join(", ");
  return {
    legalBusinessName: info.legalBusinessName || customer?.name || "",
    storeNameDba: info.storeNameDba || customer?.banner || customer?.name || "",
    physicalDeliveryAddress: info.physicalDeliveryAddress || delivery || "",
    mailingAddress: info.mailingAddress || "",
    storePhoneNumber: info.storePhoneNumber || customer?.phone || "",
    storeManager: info.storeManager || "",
    pstHstNumber: info.pstHstNumber || "",
    ownerName: contacts.ownerName || customer?.buyer || "",
    ownerPhone: contacts.ownerPhone || customer?.phone || "",
    ownerEmail: contacts.ownerEmail || customer?.email || "",
    accountsPayableName: contacts.accountsPayableName || "",
    accountsPayablePhone: contacts.accountsPayablePhone || "",
    accountsPayableEmail: contacts.accountsPayableEmail || "",
    applicationType: existing.applicationType || "",
    businessTypes: Array.isArray(existing.businessTypes) ? existing.businessTypes : [],
    otherBusinessType: existing.otherBusinessType || "",
    equipment: Array.isArray(existing.equipment?.selections) ? existing.equipment.selections : [],
    existingEquipmentType: existing.equipment?.existingEquipmentType || "",
    paymentMethods: Array.isArray(existing.paymentMethods) ? existing.paymentMethods : []
  };
}

function toggleSetupList(form, key, value) {
  const current = Array.isArray(form[key]) ? form[key] : [];
  return {
    ...form,
    [key]: current.includes(value) ? current.filter(item => item !== value) : [...current, value]
  };
}

function customerOnboardingSubmitted(customer, payload) {
  if (!customer || !payload?.customer) return;
  customer.onboardingQuestionnaire = payload.customer.onboardingQuestionnaire || customer.onboardingQuestionnaire || {};
  customer.onboardingSubmittedAt = payload.customer.onboardingSubmittedAt || new Date().toISOString();
}

function AccountSetupOverlay({ customer, onClose, profile, reason, sendPasswordResetEmail, updatePassword, updateProfile }) {
  const forcedPassword = reason === "invite" || reason === "recovery";
  const needsOnboarding = customerNeedsRetailOnboarding(profile, customer);
  const title = reason === "recovery"
    ? "Set New Password"
    : needsOnboarding
      ? "Complete Retail Setup"
      : reason === "invite"
        ? "Complete Account Setup"
        : "Finish Your Profile";
  const detail = needsOnboarding
    ? "Finish the required B2B retailer questionnaire before ordering through Ranch Power."
    : reason === "invite"
      ? "Set your password and confirm how your name should appear across Ranch Power."
      : reason === "recovery"
        ? "Create a new password for your Ranch Power account."
        : "Confirm your profile details so the rest of the system shows the right account.";
  const [displayName, setDisplayName] = useState(profile?.display_name || nameFromEmail(profile?.email));
  const [onboarding, setOnboarding] = useState(() => onboardingInitialForm(customer));
  const [nextPassword, setNextPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState(false);
  const setOnboardingField = (key, value) => setOnboarding(prev => ({ ...prev, [key]: value }));
  const toggleOnboarding = (key, value) => setOnboarding(prev => toggleSetupList(prev, key, value));

  async function submit(event) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setMessage("");
    setError(false);

    try {
      const cleanName = String(displayName || "").trim();
      if (!cleanName) throw new Error("Display name is required.");

      if (forcedPassword || nextPassword || confirmPassword) {
        if (!nextPassword || !confirmPassword) throw new Error("Enter and confirm your new password.");
        if (nextPassword !== confirmPassword) throw new Error("Passwords do not match.");
        await updatePassword(nextPassword);
      }

      if (needsOnboarding) {
        const payload = await rpApi("/api/account/onboarding", {
          method: "POST",
          body: JSON.stringify({ questionnaire: onboarding })
        });
        customerOnboardingSubmitted(customer, payload);
      }

      await updateProfile({ display_name: cleanName, setup_complete: true });
      clearAuthFlowFromUrl();
      setMessage("Account setup complete.");
      setNextPassword("");
      setConfirmPassword("");
      setTimeout(onClose, 450);
    } catch (err) {
      setError(true);
      setMessage(err && err.message ? err.message : "Could not complete account setup.");
    } finally {
      setBusy(false);
    }
  }

  async function emailReset() {
    if (busy) return;
    setBusy(true);
    setMessage("");
    setError(false);
    try {
      const payload = await sendPasswordResetEmail(profile?.email);
      setMessage(`Reset email sent to ${payload.email}.`);
    } catch (err) {
      setError(true);
      setMessage(err && err.message ? err.message : "Could not send reset email.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rp-account-setup" role="dialog" aria-modal="true" aria-labelledby="rp-account-setup-title">
      <form className="rp-account-setup__panel" onSubmit={submit}>
        <div className="rp-account-setup__brand">
          <img src="/assets/brand/buffalo.svg" alt="" />
          <div>
            <div className="rp-account-setup__eyebrow">Ranch Power</div>
            <h2 id="rp-account-setup-title">{title}</h2>
          </div>
        </div>
        <p>{detail}</p>
        <label htmlFor="rp-setup-name">Display name</label>
        <input id="rp-setup-name" autoComplete="name" value={displayName} onChange={event => setDisplayName(event.target.value)} />
        {needsOnboarding && (
          <div className="rp-onboarding-form">
            <div className="rp-onboarding-section">
              <h3>Business Information</h3>
              <label htmlFor="rp-onboarding-legal">Legal business name</label>
              <input id="rp-onboarding-legal" value={onboarding.legalBusinessName} onChange={event => setOnboardingField("legalBusinessName", event.target.value)} required />
              <label htmlFor="rp-onboarding-dba">Store name D.B.A.</label>
              <input id="rp-onboarding-dba" value={onboarding.storeNameDba} onChange={event => setOnboardingField("storeNameDba", event.target.value)} />
              <label htmlFor="rp-onboarding-delivery">Physical delivery address</label>
              <input id="rp-onboarding-delivery" value={onboarding.physicalDeliveryAddress} onChange={event => setOnboardingField("physicalDeliveryAddress", event.target.value)} required />
              <label htmlFor="rp-onboarding-mailing">Mailing address</label>
              <input id="rp-onboarding-mailing" value={onboarding.mailingAddress} onChange={event => setOnboardingField("mailingAddress", event.target.value)} />
              <div className="rp-onboarding-grid">
                <div>
                  <label htmlFor="rp-onboarding-phone">Store phone number</label>
                  <input id="rp-onboarding-phone" value={onboarding.storePhoneNumber} onChange={event => setOnboardingField("storePhoneNumber", event.target.value)} required />
                </div>
                <div>
                  <label htmlFor="rp-onboarding-manager">Store manager</label>
                  <input id="rp-onboarding-manager" value={onboarding.storeManager} onChange={event => setOnboardingField("storeManager", event.target.value)} />
                </div>
              </div>
              <label htmlFor="rp-onboarding-tax">PST/HST # if applicable</label>
              <input id="rp-onboarding-tax" value={onboarding.pstHstNumber} onChange={event => setOnboardingField("pstHstNumber", event.target.value)} />
            </div>

            <div className="rp-onboarding-section">
              <h3>Business Contacts</h3>
              <label htmlFor="rp-onboarding-owner">Name of owner(s)</label>
              <input id="rp-onboarding-owner" value={onboarding.ownerName} onChange={event => setOnboardingField("ownerName", event.target.value)} required />
              <div className="rp-onboarding-grid">
                <div>
                  <label htmlFor="rp-onboarding-owner-phone">Owner phone</label>
                  <input id="rp-onboarding-owner-phone" value={onboarding.ownerPhone} onChange={event => setOnboardingField("ownerPhone", event.target.value)} />
                </div>
                <div>
                  <label htmlFor="rp-onboarding-owner-email">Owner email</label>
                  <input id="rp-onboarding-owner-email" type="email" value={onboarding.ownerEmail} onChange={event => setOnboardingField("ownerEmail", event.target.value)} required />
                </div>
              </div>
              <label htmlFor="rp-onboarding-ap-name">Accounts payable name</label>
              <input id="rp-onboarding-ap-name" value={onboarding.accountsPayableName} onChange={event => setOnboardingField("accountsPayableName", event.target.value)} />
              <div className="rp-onboarding-grid">
                <div>
                  <label htmlFor="rp-onboarding-ap-phone">Accounts payable phone</label>
                  <input id="rp-onboarding-ap-phone" value={onboarding.accountsPayablePhone} onChange={event => setOnboardingField("accountsPayablePhone", event.target.value)} />
                </div>
                <div>
                  <label htmlFor="rp-onboarding-ap-email">Accounts payable email</label>
                  <input id="rp-onboarding-ap-email" type="email" value={onboarding.accountsPayableEmail} onChange={event => setOnboardingField("accountsPayableEmail", event.target.value)} />
                </div>
              </div>
            </div>

            <div className="rp-onboarding-section">
              <h3>Application</h3>
              <div className="rp-onboarding-choice-row">
                {["New Store", "Ownership Change"].map(value => (
                  <label className="rp-onboarding-choice" key={value}>
                    <input type="radio" name="rp-application-type" checked={onboarding.applicationType === value} onChange={() => setOnboardingField("applicationType", value)} />
                    <span>{value}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="rp-onboarding-section">
              <h3>Type of Business</h3>
              <div className="rp-onboarding-checks">
                {["Convenience Store", "Restaurant/Bar", "Liquor Store", "Hotel", "Retail Store", "Superette", "Drug Store", "Seasonal", "Hardware Store", "Other"].map(value => (
                  <label className="rp-onboarding-choice" key={value}>
                    <input type="checkbox" checked={onboarding.businessTypes.includes(value)} onChange={() => toggleOnboarding("businessTypes", value)} />
                    <span>{value}</span>
                  </label>
                ))}
              </div>
              {onboarding.businessTypes.includes("Other") && (
                <>
                  <label htmlFor="rp-onboarding-other-type">Other business type</label>
                  <input id="rp-onboarding-other-type" value={onboarding.otherBusinessType} onChange={event => setOnboardingField("otherBusinessType", event.target.value)} />
                </>
              )}
            </div>

            <div className="rp-onboarding-section">
              <h3>Equipment</h3>
              <div className="rp-onboarding-choice-row">
                {["Need New Cooler Equipment", "Existing Equipment"].map(value => (
                  <label className="rp-onboarding-choice" key={value}>
                    <input type="checkbox" checked={onboarding.equipment.includes(value)} onChange={() => toggleOnboarding("equipment", value)} />
                    <span>{value}</span>
                  </label>
                ))}
              </div>
              {onboarding.equipment.includes("Existing Equipment") && (
                <>
                  <label htmlFor="rp-onboarding-equipment-type">Existing equipment type</label>
                  <input id="rp-onboarding-equipment-type" value={onboarding.existingEquipmentType} onChange={event => setOnboardingField("existingEquipmentType", event.target.value)} />
                </>
              )}
            </div>

            <div className="rp-onboarding-section">
              <h3>Payment Method</h3>
              <div className="rp-onboarding-checks rp-onboarding-checks--single">
                {["COD", "e-Transfer", "Pre-Authorized Debit", "Pre-Authorized Credit"].map(value => (
                  <label className="rp-onboarding-choice" key={value}>
                    <input type="checkbox" checked={onboarding.paymentMethods.includes(value)} onChange={() => toggleOnboarding("paymentMethods", value)} />
                    <span>{value}</span>
                  </label>
                ))}
              </div>
              <p className="rp-onboarding-payment-note">{RETAIL_QUOTE_CREDIT_CARD_CHARGE_NOTE}</p>
            </div>
          </div>
        )}
        <label htmlFor="rp-setup-password">New password{forcedPassword ? "" : " (optional)"}</label>
        <input id="rp-setup-password" autoComplete="new-password" type="password" value={nextPassword} onChange={event => setNextPassword(event.target.value)} placeholder="At least 12 characters" />
        <label htmlFor="rp-setup-confirm">Confirm password</label>
        <input id="rp-setup-confirm" autoComplete="new-password" type="password" value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} />
        {message && <div className={error ? "rp-auth-message rp-auth-error" : "rp-auth-message"}>{message}</div>}
        <button type="submit" disabled={busy}>{busy ? "Saving..." : needsOnboarding ? "Complete Setup" : "Save Account"}</button>
        <button className="secondary" type="button" disabled={busy} onClick={emailReset}>Email reset link</button>
        {!forcedPassword && !needsOnboarding && <button className="secondary" type="button" disabled={busy} onClick={onClose}>Do later</button>}
      </form>
    </div>
  );
}

function AuthFrame({ children, detail, error, message, title = "Ranch Power" }) {
  return (
    <div className="rp-auth-shell">
      <section className="rp-auth-panel" aria-live="polite">
        <div className="rp-auth-panel__brand">
          <img src="/assets/brand/buffalo.svg" alt="" />
          <div>
            <h1>{title}</h1>
            <p style={{ margin: "4px 0 0" }}>{modeLabel()} workspace</p>
          </div>
        </div>
        {detail && <p>{detail}</p>}
        {children}
        <div className={`rp-auth-message ${error ? "rp-auth-error" : ""}`}>{message}</div>
      </section>
    </div>
  );
}
